import React from "react";
import {
    BooleanRadioButton,
    BooleanRadioButtonGroup,
    RadioButton,
    RadioButtonGroup
} from "@octopusdeploy/design-system-components";
import {HostedInstanceResource} from "../../../client/resources/hostedInstanceResource";
import {BusyState} from "../../../components/BusyFromPromise/BusyFromPromise";
import {DataBaseComponent, DataBaseComponentState, Errors} from "../../../components/DataBaseComponent";
import {RunSqlRequest, SqlOutputFormat} from "../../../client/resources/runSqlRequest";
import SaveDialogLayout from "../../../components/DialogLayout/SaveDialogLayout";
import { Callout } from "@octopusdeploy/design-system-components";
import CommandTimeoutSelect from "shared/CommandTimeoutSelect";
import {Text} from "components/form";
import repository from "../../../client/repository";
import {getRememberedJustification, rememberJustification} from "../../../client/rememberedJustification";

interface RunSqlDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    onSaveClick(): Promise<boolean>;
}

interface RunSqlDialogState extends DataBaseComponentState {
    runSqlRequest: Partial<RunSqlRequest>;
}

export class RunSqlDialog extends DataBaseComponent<RunSqlDialogProps, RunSqlDialogState> {
    constructor(props: RunSqlDialogProps) {
        super(props);
        this.state = {
            runSqlRequest: {
                CommandTimeout: "0:05:00",
                RunSqlDuringOutageWindow: false,
                IsQuery: true,
                ExecutionPlanOptions: "None",
                OutputFormat: "Json",
                Permission: getRememberedJustification()
            }
        }
    }

    render() {
        const request = this.state.runSqlRequest;

        const showEstimatedPlanCallout = this.state.runSqlRequest.ExecutionPlanOptions === "EstimatedPlanOnly";

        return <SaveDialogLayout title={this.getTitle()}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Run"}
                                 cancelButtonLabel={"Cancel"}
                                 onSaveClick={() => this.runSql(request)}>

            <Callout type="warning" title="Warning">
                <li>Obtain <strong>permission</strong> from the Instance owner before running a query.</li>
                <li>Ensure the query will not cause outage or <strong>performance</strong> issues, especially if run during the outage window when nobody is watching.</li>
                <li>Ensure your SQL is <strong>idempotent</strong> as it <strong>may be run multiple times</strong> due to Cloud Portal's built-in retry logic.</li>
            </Callout>

            <BooleanRadioButtonGroup title="Scheduling" value={this.state.runSqlRequest.RunSqlDuringOutageWindow} onChange={RunSqlDuringOutageWindow => this.updateRunSqlRequest({RunSqlDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Run During Outage Window" />
                <BooleanRadioButton value={false} label="Run Immediately" />
            </BooleanRadioButtonGroup>

            <BooleanRadioButtonGroup title="Execution Mode" value={this.state.runSqlRequest.IsQuery} onChange={IsQuery => this.updateRunSqlRequest({IsQuery})}>
                <BooleanRadioButton value={true} label="Run Query (e.g. Select)" />
                <BooleanRadioButton value={false} label="Run Command (e.g. Update/Insert/Delete)" />
            </BooleanRadioButtonGroup>

            <RadioButtonGroup title="Output Format" value={this.state.runSqlRequest.OutputFormat} onChange={(OutputFormat: SqlOutputFormat) => this.updateRunSqlRequest({OutputFormat})}>
                <RadioButton value="Json" label="Json" />
                <RadioButton value="Csv" label="Csv" />
            </RadioButtonGroup>

            <CommandTimeoutSelect value={this.state.runSqlRequest.CommandTimeout}
                                  onChange={CommandTimeout => this.updateRunSqlRequest({CommandTimeout})}
            />

            <Text label="Permission"
                  hintText="Link to email, support ticket, etc."
                  value={this.state.runSqlRequest.Permission}
                  onChange={Permission => this.updateRunSqlRequest({Permission})}
                  autoFocus={true}
                  multiLine={true}
                  rows={2}
            />

            <Text label="SQL"
                  hintText="SQL"
                  value={this.state.runSqlRequest.Sql}
                  onChange={Sql => this.updateRunSqlRequest({Sql})}
                  multiLine={true}
                  rows={10}
            />

            <RadioButtonGroup title="Execution Plan" value={this.state.runSqlRequest.ExecutionPlanOptions} onChange={ExecutionPlanOptions => this.updateRunSqlRequest({ExecutionPlanOptions})}>
                <RadioButton value="None" label="None" />
                <RadioButton value="IncludeActualPlan" label="Include Actual" />
                <RadioButton value="EstimatedPlanOnly" label="Estimated Plan Only" />
            </RadioButtonGroup>

            {showEstimatedPlanCallout && <Callout type="information" title="No query results will be returned." />}

        </SaveDialogLayout>;
    }

    private getTitle(): string {
        if (this.props.instance) {
            return `Run SQL for ${this.props.instance.DnsPrefix} (${this.props.instance.ShortId})`;
        }
        return "Run SQL";
    }

    private updateRunSqlRequest(update: Partial<RunSqlRequest>) {
        return this.setState(prevState => ({runSqlRequest: {...prevState.runSqlRequest, ...update}}));
    }

    private runSql(request: Partial<RunSqlRequest>) {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.runSql(this.props.instance, request as RunSqlRequest);
            rememberJustification(request.Permission);
            await this.props.onSaveClick();
            return true;
        })
    }
}
